import Promise from 'bluebird';
import axios from 'axios';
import Localization from 'devextreme/localization.js';
import api from 'view/carto/api';
import Auth from 'src/auth.js';
import _ from 'lodash';
import LocaleCurrency from 'locale-currency';
import moment from 'moment';
import $ from 'jquery';
import appConfig from 'app-core/config';
import langmap from 'langmap';
import campaignData from 'view/campaign/campaign-data';
import networkShowData from 'view/networkShow/network-show-data';
import { postDurableFunction, getSasForAzureContainer, uploadBlobWithInfoResponse, deleteBlob, getFileAudioBlob } from 'app-core/azure';
import { resampleBlob } from 'view/audioTracker/audio-helper';

let objectsCache = {};
const unlimited = 9999;

function getCountries() {
	return Promise.resolve([{ name: 'USA', states: true }]);
}

let states = null;
function getStates(country) {
	if (states) {
		return Promise.resolve(states.filter(f => f.country === country));
	}
	return getCartoSql(`
		SELECT distinct(a_state) as name, 'USA' as "country"
		FROM alluszipswithdata
	`).then(r => {
		states = r.reduce((arr, f) => {
			let splitStates = (`${f.name}`.split(/,|;/).map(n => ({ ...f, name: n.trim() })));
			arr.push(...splitStates.filter(s => !arr.some(a => a.name === s.name)));
			return arr;
		}, []);
		if (__DEBUG__)
			console.info('States loaded', { states, country });
		return _.sortBy(states.filter(f => f.country === country), ['name']);
	});
}

function getCities(country, state) {
	return getCartoSql(`
		SELECT distinct(a_city) as name
		FROM alluszipswithdata
		where a_state = '${state}'
	`).then(r => {
		const cities = r.reduce((arr, f) => {
			let splitCities = (`${f.name}`.split(/,|;/).map(n => ({ ...f, name: n.trim() })));
			arr.push(...splitCities.filter(s => s.name && !arr.some(a => a.name === s.name)));
			return arr;
		}, []);
		if (__DEBUG__)
			console.info('Cities loaded', { states, country, cities });
		return _.sortBy(cities, ['name']);
	});
}

function getLocationGeoJson(params) {
	params = _.assign({
		street: null,
		city: null,
		county: null,
		state: null,
		country: null,
		postalcode: null,
		q: null,
		polygon_geojson: 1,
		format: 'json',
		limit: 1
	}, params);

	return axios.get('https://nominatim.openstreetmap.org/search.php', { params }).then(r => {
		return r.data;
	});
}

function getHostAddress() {
	return __DEBUG__ ? /*'http://localhost:56800'*/ '' : window.location.origin;
}

let multipleCallsCache = {};
function reduceMultipleCalls(params, promiseFunc) {
	try {
		let strParams = JSON.stringify(params);
		let callItem = multipleCallsCache[strParams];
		if (callItem?.executed === false) {
			if (__DEBUG__)
				console.debug('Reduced multiple call', { params });
			return callItem.promise;
		} else {
			if (!callItem) {
				callItem = {};
				multipleCallsCache[strParams] = callItem;
			}
			callItem.executed = false;
			callItem.promise = promiseFunc().finally((r) => {
				callItem.executed = true;
				return r;
			});
			return callItem.promise;
		}
	} catch {
		return promiseFunc();
	}
}

function canShowErrorMessage(message) {
	var hidden = ['IDX10223', 'IDX12709'];
	return !hidden.some(f => `${message}`.indexOf(f) > -1);
}

function callMethod(methodName, params, config, isPost) {
	const loadOpts = _.assign({
		params,
		headers: {
			authToken: Auth.accessToken(),
			'accept-language': Auth.currentLocale(),
			...__HEADERS__
		}
	}, config || {});

	return reduceMultipleCalls({ methodName, loadOpts }, () => {
		if (__DEBUG__)
			console.info('Call method', methodName, params, { loadOpts });

		return axios({
			method: isPost ? 'post' : 'get',
			url: `${getHostAddress()}/data/${methodName}`,
			...loadOpts
		})
			.then(r => {
				let errorMessage = r?.data?.ErrorMessage || r?.data?.ExceptionMessage;
				if (__DEBUG__) {
					console.log('%cGET', 'background: green; color: white; border-radius: 4px; padding: 0px 4px;', `Call method data loaded ${`${params?.method}/${params?.parameters}`}`, { methodName, params, r });
				}
				if (errorMessage) {
					if (__DEBUG__)
						console.error(errorMessage);
					if (`${errorMessage}`.indexOf('Token does not have a kid') > -1) {
						Auth.refreshToken(true);
					} else {
						if (!config?.silent && canShowErrorMessage(errorMessage))
							appConfig.notifyError(errorMessage);
						checkLogout(r?.data?.ErrorCode, errorMessage);
						throw (errorMessage);
					}
				}
				return r.data;
			})
			.catch(r => {
				if (__DEBUG__)
					console.error(`Error on call method ${methodName}?${params}`, { result: r });
				checkLogout(r.response?.status);
				throw (r && r.response && r.response.data);
			});
	});
}

function checkLogout(status, message) {
	if (`${message}`.indexOf('IDX10223') > -1 || status == 401) {
		Auth.logOut();
		appConfig.getVueApp().$router.push({ path: '/login-form' });
		appConfig.notifyError('Your session has expired. Please relogin.');
	}
}

function postMethod(methodName, config) {
	const loadOpts = _.assign({
		url: `${getHostAddress()}/data/${methodName}`,
		method: 'POST',
		headers: {
			authToken: Auth.accessToken(),
			'accept-language': Auth.currentLocale(),
			...__HEADERS__
		}
	}, config || {});

	if (__DEBUG__)
		console.info('Post method', methodName, { loadOpts });

	return new Promise((resolve, reject) => {
		$.ajax(loadOpts).then(r => {
			if (__DEBUG__) {
				console.info('%cPOST', 'background: blue; color: white; border-radius: 4px; padding: 0px 4px;', `Post method ${methodName} executed`, { methodName, loadOpts, r });
				window.lastPostData = loadOpts;
			}

			let errorMessage = r && r.ErrorMessage;
			if (errorMessage) {
				if (__DEBUG__)
					console.error(errorMessage);
				if (`${errorMessage}`.indexOf('Token does not have a kid') > -1) {
					Auth.refreshToken(true);
				} else {
					if (!config?.SilentMode && canShowErrorMessage(errorMessage))
						appConfig.notifyError(errorMessage);
					checkLogout(r?.ErrorCode, errorMessage);
					throw (errorMessage);
				}
			}

			resolve(r);
		}).fail((xhr, text) => {
			if (__DEBUG__)
				console.debug('Post method error', { xhr, text });
			if (canShowErrorMessage(text || xhr))
				appConfig.notifyError(text || xhr);
			checkLogout(void 0, text || xhr);
			console.error(text || xhr);
			reject(new Error(text || xhr));
			return xhr;
		});
	});
}

let iabCategories = [];
function loadCategories() {
	if (iabCategories.length)
		return Promise.resolve(iabCategories);
	return getQuoteApi('General/GetIabCategoriesV2').then(r => {
		iabCategories = r.map(f => ({
			Oid: f.Oid,
			UniqueID: f.Oid,
			ParentID: f.ParentId || null,
			Name: f.Name,
			Code: f.Code
		}));

		if (__DEBUG__)
			console.log('Categories loaded', { r, iabCategories });

		return iabCategories;
	});
}

let externalIabCategories = [];
function loadIabCategories() {
	if (externalIabCategories.length)
		return Promise.resolve(externalIabCategories);
	return getExternalApi('IabCategory/GetIabCategoriesV2').then(r => {
		externalIabCategories = r.map(f => ({
			...f,
			ParentId: f.ParentId || null
		}));

		if (__DEBUG__)
			console.log('Categories loaded', { r, externalIabCategories });

		return externalIabCategories;
	});
}

let employees = [];
function loadEmployees() {
	if (employees.length)
		return Promise.resolve(employees);
	return getQuoteApi('Security/GetEmployees').then(r => {
		employees = r.map(f => ({
			...f,
			DisplayName: f.Title || f.UserName
		}));
		return employees;
	});
}

let companies = [];
function loadCompanies() {
	if (companies.length)
		return Promise.resolve(companies);
	return getQuoteApi('Company/GetCompanies').then(r => {
		companies = r.Results;
		return companies;
	});
}

function getCompany(id) {
	return getQuoteApi('Company/GetCompany', `companyId=${id}`);
}

const adtProductTypes = [
	{ type: 1, id: 'podcast', name: 'Podcast', icon: 'fas fa-microphone-alt', components: ['budget', 'display'] },
	{ type: 2, id: 'display', name: 'Display', description: 'w/ Geofencing', icon: 'fas fa-ad', components: ['budget', 'display', 'geofencing', 'remarketing'] },
	{ type: 3, id: 'radio', name: 'Airtime', icon: 'fas fa-broadcast-tower', components: [], recurrent: false },
	{ type: 4, id: 'connectedtv', name: 'Connected TV', icon: 'fas fa-network-wired', components: ['budget', 'display'] },
	{ type: 5, id: 'sem', name: 'SEM', icon: 'fas fa-search', components: ['budget'], highlight: '90% + OF OPPORTUNITY' },
	{ type: 6, id: 'television', name: 'Television', icon: 'fas fa-tv', components: ['budget', 'display'] },
	{ type: 7, id: 'annual', name: 'Listed local - annual', description: 'Rate', icon: 'fas fa-compass', components: ['budget', 'display'] },
	{ type: 8, id: 'audio', name: 'Audio', icon: 'fas fa-volume-up', components: ['budget', 'display'] },
	{ type: 9, id: 'video', name: 'Video', description: 'w/ Geo Video', icon: 'fas fa-video', components: ['budget', 'display'] }
];
let products = null;
function loadProducts() {
	if (products) return Promise.resolve(products);
	return getQuoteApi('General/GetDapCategories').then(r => {
		products = r.map(f => ({ ...adtProductTypes.filter(p => p.type === f.Type)[0], name: f.Name, type: f.Type, oid: f.Oid, lineType: f.LineType }));
		if (__DEBUG__)
			console.debug('Products loaded', { products, r, adtProductTypes });
		return products;
	});
}

function formatNumber(num, type, precision) {
	if (precision === void 0) {
		return Localization.formatNumber(num, type);	
	}
	return Localization.formatNumber(num, {
		type: type || 'currency',
		precision: precision || 0
	});
}

function getCampaignFromQuote(quote) {
	return {
		...quote,
		AdvertiserName: quote.Account && quote.Account.Name,
		Budget: quote.NettNettAmount
	};
}

function getCampaigns(filter, page) {
	let params = [
		filter !== null && `filterType=${filter}`,
		page && `page=${page}`
	];
	return getQuoteApi('quote/GetCampaigns', params.filter(f => !!f).join('&')).then(r => {
		if (__DEBUG__)
			console.info('Campaigns loaded', r);
		let sum = r.CustomSummaries || {};
		return {
			PageNumber: r.PageNumber,
			TotalNumberOfPages: r.TotalNumberOfPages,
			TotalNumberOfRecords: r.TotalNumberOfRecords,
			TotalOrderedSpots: !r.TotalNumberOfRecords ? 0 : sum.TrafficTotalSpots,
			TotalOrderedImpressions: !r.TotalNumberOfRecords ? 0 : sum.TotalTargetImpressions,
			Results: r.Results.map(f => getCampaignFromQuote(f))
		};
	});
}

function getExternalCampaigns(pageIndex, limit, opts) {
	if (opts && opts.byKey)
		return getExternalApi('campaign/GetCampaign', `campaignId=${opts.byKey}`).then(r => ({ Results: [r] }));
	const type = `&campaignType=${opts?.type == null ? 1 : opts.type}`;
	const sorting = opts?.sort && `&sorting=${encodeURIComponent(JSON.stringify(opts.sort))}` || '';
	return getExternalApi('campaign/GetCampaigns', `page=${pageIndex + 1}${type}${sorting}`, limit);
}

function getExternalCampaign(id) {
	return getExternalApi('campaign/GetCampaign', `campaignId=${id}`);
}

function getExternalCampaignCreatives(pageIndex, limit, opts, campaignId) {
	if (opts && opts.byKey)
		return getExternalApi('Creative/GetCreative', `creativeId=${opts.byKey}`).then(r => ({ Results: [r] }));
	return getExternalApi('campaign/GetCampaignCreatives', `campaignId=${campaignId}&page=${(pageIndex + 1) || 1}`);
}

function getMaterialLinks(creativeId, pageIndex) {
	return getExternalApi('creative/GetMaterialLinks', `creativeId=${creativeId}&page=${pageIndex + 1}`);
}

function getInsertionsByCreative(creativeId) {
	return getExternalApi('creative/GetAllRootInsertionsByCreative', `creativeId=${creativeId}`, unlimited);
}

function getSsmlInsertionsByEmail(creativeId) {
	return getExternalApi('Creative/GetSsmlInsertionsByEmail', `creativeId=${creativeId}`);
}

function createUpdateInsertion(r) {
	if (r?.__isDeleted || r?.__inMemory) return Promise.resolve(r);
	let type = r.type || r.Type || '';
	if (type === 'textToSpeech' || type.indexOf('SsmlInsertion') > -1)
		return createUpdateSsmlInsertion(r);
	if (type === 'audioFile' || type.indexOf('AudioInsertion') > -1)
		return createUpdateAudioInsertion(r);
	if (type === 'video')
		return createUpdateVideoInsertion(r);
}

function createUpdatePureInsertion(r) {
	if (r?.__isDeleted || r?.__inMemory) return Promise.resolve(r);
	if (!r.JsonConfig)
		r.JsonConfig = {};
	delete r.base64;
	let type = r.type || r.Type || '';
	if (type === 'textToSpeech' || type.indexOf('SsmlInsertion') > -1)
		return postExternalApi('creative/CreateUpdateSsmlInsertion', { ...r, __track: null });
	if (type === 'audioFile' || type.indexOf('AudioInsertion') > -1)
		return postExternalApi('creative/CreateUpdateAudioInsertion', { ...r, __track: null });
	if (type === 'break' || type.indexOf('BreakInsertion') > -1)
		return postExternalApi('creative/CreateUpdateBreakInsertion', { ...r, __track: null });
	if (type === 'video' || type.indexOf('VideoInsertion') > -1)
		return postExternalApi('creative/CreateUpdateVideoInsertion', { ...r, __track: null });
}

function createUpdateSsmlInsertion(r) {
	if (r?.__isDeleted || r?.__inMemory) return Promise.resolve(r);
	return postExternalApi('creative/CreateUpdateSsmlInsertion', {
		Text: r.text || null,
		Ssml: r.ssml || null,
		Language: r.language,
		Voice: r.voice,
		VoiceStyle: r.voiceStyle,
		Rate: r.rate,
		Pitch: r.pitch,
		Oid: r.oid || null,
		Name: r.name,
		StartTime: parseInt(r.startTime),
		StartOffset: parseInt(r.startOffset) || null,
		EndOffset: parseInt(r.endOffset) || null,
		Duration: parseInt(r.duration),
		EndTime: parseInt(r.startTime) + parseInt(r.duration),
		CreativeId: r.creativeId,
		ParentId: r.parentId,
		EventRule: r.eventRuleId ? { Oid: r.eventRuleId } : null,
		JsonConfig: r.jsonConfig || {},
		DynamicSsmlResult: r.dynamicSsmlResult,
		LastTtsLogId: r.lastTtsLogId,
		TtsProviderType: r.ttsProviderType,
		VoiceSettings: r.voiceSettings
	});
}

function createUpdateAudioInsertion(r) {
	if (r?.__isDeleted || r?.__inMemory) return Promise.resolve(r);
	return postExternalApi('creative/CreateUpdateAudioInsertion', {
		Oid: r.oid || null,
		Name: r.name,
		StartTime: parseInt(r.startTime),
		Duration: parseInt(r.duration),
		EndTime: parseInt(r.startTime) + parseInt(r.duration),
		StartOffset: parseInt(r.startOffset) || null,
		EndOffset: parseInt(r.endOffset) || null,
		CreativeId: r.creativeId,
		ParentId: r.parentId,
		EventRule: r.eventRuleId ? { Oid: r.eventRuleId } : null,
		JsonConfig: r.jsonConfig || {}
	});
}

function createUpdateVideoInsertion(r) {
	if (r?.__isDeleted || r?.__inMemory) return Promise.resolve(r);
	return postExternalApi('creative/CreateUpdateAudioInsertion', {
		Oid: r.oid || null,
		Name: r.name,
		StartTime: parseInt(r.startTime),
		Duration: parseInt(r.duration),
		EndTime: parseInt(r.startTime) + parseInt(r.duration),
		StartOffset: parseInt(r.startOffset) || null,
		EndOffset: parseInt(r.endOffset) || null,
		CreativeId: r.creativeId,
		ParentId: r.parentId,
		EventRule: r.eventRuleId ? { Oid: r.eventRuleId } : null,
		JsonConfig: r.jsonConfig || {}
	});
}

function createUpdateCreative(creative) {
	return postExternalApi('creative/CreateUpdateCreative', creative);
}

function formatObjectToLowerCase(obj) {
	return _.transform(obj, (result, val, key) => {
		result[`${key[0].toLowerCase()}${key.substr(1)}`] = val;
	});
}

function formatObjectToUpperCase(obj) {
	return _.transform(obj, (result, val, key) => {
		result[`${key[0].toUpperCase()}${key.substr(1)}`] = val;
	});
}

function getCampaign(quoteNumber) {
	let campaign = objectsCache[`campaign_${quoteNumber}`];
	if (campaign)
		return Promise.resolve(campaign);

	return getQuoteApi('quote/GetQuote', `number=${quoteNumber}`).then(r => {
		campaign = getCampaignFromQuote(r);
		objectsCache[`campaign_${quoteNumber}`] = campaign;
		return campaign;
	});
}

function getMethodUrl(methodName) {
	return `${getHostAddress()}/data/${methodName}`;
}

function getBlob(link, asBlob, opts) {
	return new Promise((resolve) => {
		const conf = {
			responseType: 'blob',
			onDownloadProgress: opts?.downloadProgress
		};
		axios.get(link, conf)
			.then(response => {
				if (__DEBUG__)
					console.info('getBlob', { response });

				if (asBlob) {
					resolve(response.data);
					return;
				}

				let fr = new FileReader;
				fr.readAsDataURL(response.data);
				fr.onloadend = () => {
					let value = fr.result;
					if (opts?.contentType && value?.indexOf('data:application/octet-stream') > -1) {
						value = value.replace('data:application/octet-stream', `data:${opts?.contentType}`)
					}
					resolve(value);
				};
			})
			.catch(err => {
				console.error(`Failed to get file '${link}'`);
				console.log(err);
				resolve(null);
			});
	});
}

function getDeliveredCampaignData(quoteNumber) {
	if (!quoteNumber) return;
	if (__DEBUG__)
		console.info('getDeliveredCampaignData', { quoteNumber });
	return getTop500Zips().then(zips => {
		if (__DEBUG__)
			console.info('Zips loaded', zips);
		return loadProducts().then(products => getQuoteApi('booking/GetDeliveredValuesByQuote', `quoteNumber=${quoteNumber}`).then(r => {
			if (__DEBUG__)
				console.info('DeliveredCampaignData loaded', r);
			return r.map(f => {
				let zipData = zips[0];
				let polygon = JSON.parse(zipData.geom);
				let location = polygon.coordinates[0][0];

				return {
					camapignId: f.QuoteNumber,
					product: products[0].id,
					date: f.Date,
					impressions: f.Impressions,
					clicks: f.Clicks,
					displayRemarketingImpressions: f.DisplayRetargetingImpressions,
					displayRemarketingClicks: f.DisplayRetargetingClicks,
					ctr: Number(parseFloat(f.Clicks / f.Impressions).toFixed(4)),
					location: {
						geo: {
							lat: location[1],
							lng: location[0]
						}
					},
					engagements: f.Engagements,
					visits: f.Visits,
					viewThroughs: f.ViewThroughs,
					domain: f.Domain,
					device: f.DeviceType,
					zip: zipData.zip,
					city: `${zipData.city}`.split(';')[0]
				};
			});
		}));
	});
}

function getTop500Zips() {
	return getCartoSql(`
		select * from (
			SELECT ST_asGeoJSON(ST_Boundary(the_geom)) as geom, zcta5ce10 as zip, a_city as city
			FROM alluszipswithdata
		) data
		where city is not null group by zip, geom, city LIMIT 500
	`);
}

let promiseCache = {};
function getCartoSql(query, clear) {
	if (__DEBUG__)
		console.debug('Get carto sql', { query });
	if (promiseCache[query]) {
		if (clear)
			promiseCache[query] = null;
		else {
			if (__DEBUG__)
				console.debug('Carto sql loaded from cache');
			return promiseCache[query];
		}
	}

	const promise = new Promise(resolve => {
		$.ajax({
			url: `https://${api.username}.carto.com/api/v2/sql?api_key=${api.apiKey}`,
			method: 'POST',
			data: { q: query }
		}).then(response => {
			if (__DEBUG__)
				console.log('Carto sql loaded', { query, response });
			resolve(response.rows);
			return response.rows;
		});
	});

	promiseCache[query] = promise;
	return promise;
}

function formatLocaleCurrency(num) {
	let language = window.navigator.userLanguage || window.navigator.language;
	return num.toLocaleString(language, { style: 'currency', currency: LocaleCurrency.getCurrency(language) });
}

function currencySign() {
	return formatLocaleCurrency(0)
		.replace(new RegExp('0', 'g'), '')
		.replace(new RegExp(',', 'g'), '')
		.replace(new RegExp('\\.', 'g'), '')
		.replace(new RegExp(' ', 'g'), '')
		.trim();
}

function getEducationLevels() {
	return Promise.resolve([
		{ id: 1, name: 'No college' },
		{ id: 2, name: 'College' },
		{ id: 3, name: 'Grad school' }
	]);
}

function getPodcasts(pageIndex, limit, params) {
	return getQuoteApi('podcast/GetPodcasts', `page=${pageIndex + 1}${params ? `&${params}` : ''}`, limit).then(r => ({
		total: r.TotalNumberOfRecords,
		items: r.Results.map(f => ({
			id: f.Oid,
			name: f.Name,
			reach: f.Reach,
			image: getMethodUrl(`quoteApi?accessToken=${Auth.accessToken()}&method=Podcast/GetPodcastImage&parameters=${encodeURIComponent(`podcastId=${f.Oid}&accessToken=${Auth.accessToken()}`)}`),
			cpm: f.Rate,
			cost: f.Reach / 100 * f.Rate,
			description: f.Description
		}))
	}));
}

function getPackages(pageIndex, limit, params) {
	return getQuoteApi('Quote/GetSalesPackages', `page=${pageIndex + 1}${params ? `&${params}` : ''}`, limit).then(r => ({
		total: r.TotalNumberOfRecords,
		items: r.Results
	}));
}

function getNetworks() {
	return getQuoteApi('Station/GetNetworks').then(r => r.map(f => ({
		id: f.Oid,
		name: f.Name,
		code: f.NetworkCode
	})));
}

function getAdPlacements() {
	return Promise.resolve([
		{ id: 1, name: 'pre roll' },
		{ id: 2, name: 'mid roll' },
		{ id: 3, name: 'post roll' },
	]);
}

function getStreamings(pageIndex, limit, params) {
	const body = {
		Page: pageIndex,
		...params
	};
	return postQuoteApi(`streaming/GetStreamings&pageSize=${limit}`, body).then(r => ({
		total: r.TotalNumberOfRecords,
		summaries: formatObjectToLowerCase(r.CustomSummaries),
		items: r.Results.map(f => ({
			id: f.Oid,
			duration: f.Length,
			price: f.RateCardPrice,
			logo: getMethodUrl(`quoteApi?accessToken=${Auth.accessToken()}&method=Podcast/GetPodcastImage&parameters=${encodeURIComponent(`podcastId=${f.Property.Oid}`)}&accessToken=${Auth.accessToken()}`),
			adPlacement: ['', 'pre roll', 'mid roll', 'post-roll'][f.AdRunType],
			name: f.Property && f.Property.Name,
			cpm: f.AgreedPrice * 1000 / f.EstimatedImpressions,
			impressions: f.EstimatedImpressions,
			agreedPrice: f.AgreedPrice,
			dto: 100 * (f.RateCardPrice - f.AgreedPrice) / f.RateCardPrice,
			adRunType: f.AdRunType,
			ratingMethod: f.RatingMethod
		}))
	}));
}

function formatDate(date, format) {
	if (!format) return '';
	return moment(date).format(format);
}

function getBase64AudioExtension(base64) {
	let ext = 'wav';
	if (base64.indexOf('audio/mpeg') > -1)
		ext = 'mp3';
	if (base64.indexOf('audio/ogg') > -1)
		ext = 'ogg';
	if (base64.indexOf('video/mp4') > -1)
		ext = 'mp4';
	return ext;
}

function getBase64ContentType(fileName) {
	let type = 'audio/wav';
	if (fileName?.endsWith('.mp3'))
		type = 'audio/mpeg';
	if (fileName?.endsWith('.ogg'))
		type = 'audio/ogg';
	if (fileName?.endsWith('.mp4'))
		type = 'video/mp4';
	return type;
}

function toggleSelectedIdInArray(id, array) {
	let idx = array.indexOf(id);
	if (idx === -1)
		array.push(id);
	else
		array.splice(idx, 1);
}

function createProposal(proposal) {
	let images = [
		{ id: 'ImageGeographies', dataUri: proposal.imageGeographies },
		{ id: 'ImageGeotargeting', dataUri: proposal.imageGeotargeting },
		{ id: 'ImageGeofencing', dataUri: proposal.imageGeofencing }
	].filter(f => f.dataUri);

	delete proposal.imageGeographies;
	delete proposal.imageGeotargeting;
	delete proposal.imageGeofencing;

	if (__DEBUG__)
		console.debug('Sending proposal', proposal);

	let getDataType = (dataUri) => dataUri.split('data:')[1].split(';base64,')[0];
	let getBase64 = (dataUri) => dataUri.split(';base64,')[1];

	return postQuoteApi('quote/CreateUpdateProposal', proposal).then(r => {
		if (!r.Success) throwEx(r.ErrorMessage);
		let oid = r.Data;
		if (__DEBUG__)
			console.debug('Sync proposal images', { images });
		if (images.length) {
			return postQuoteApi('quote/AddImages', {
				ObjectId: oid,
				Data: images.map(f => ({
					TargetField: f.id,
					DataType: getDataType(f.dataUri),
					Base64Content: getBase64(f.dataUri)
				}))
			}).then(r => {
				if (__DEBUG__)
					console.debug('Images saved', { r });
				return oid;
			});
		}

		if (__DEBUG__)
			console.debug('Proposal saved', { r });
		return oid;
	});
}

function throwEx(error) {
	appConfig.notifyError(error);
	throw error;
}

function getProposals(pageIndex, limit, opts) {
	return postQuoteApi(`quote/GetProposals?page=${pageIndex + 1}&pageSize=${limit}`, {
		Page: pageIndex + 1,
		SortProperties: !opts || !opts.sorting ? null : opts.sorting.map(f => (
			{
				FieldName: f.fieldName,
				Direction: f.direction === 'desc' ? 1 : 0
			}
		))
	}).then(r => ({
		total: r.TotalNumberOfRecords,
		items: r.Results.filter(f => f.ProposalJson).map(f => JSON.parse(f.ProposalJson))
	}));
}

function getRatingMethods() {
	return Promise.resolve([
		'PerDay',
		'PerWeek',
		'PerMonth',
		'PerYear',
		'CPM',
		'PerClick',
		'PerEmail',
		'PerVideo',
		'PerSalesLead',
		'PerTransaction',
		'PerRecord',
		'PerActivity',
		'Sponsorship',
		'PerSpotPerDay',
		'PerSpot'
	].map((f, i) => ({
		id: i + 1,
		name: spaceByUpperCase(f, true)
	})));
}

function spaceByUpperCase(s1, makeLower) {
	let s2 = s1.toLowerCase();
	let result = '';
	for (let i = 0; i < s1.length; i++) {
		if (s1[i] !== s2[i] && s1[i + 1] === s2[i + 1] && s1[i - 1] === s2[i - 1])
			result = `${result} ${makeLower && i > 0 ? s1[i].toLowerCase() : s1[i]}`;
		else
			result = result + s1[i];
	}
	return result;
}

function xmlToJson(xml) {
	// Create the return object
	let obj = {};

	if (xml.nodeType === 1) {
		// element
		// do attributes
		if (xml.attributes.length > 0) {
			obj['_attrs'] = {};
			for (let j = 0; j < xml.attributes.length; j++) {
				let attribute = xml.attributes.item(j);
				obj['_attrs'][attribute.nodeName] = attribute.nodeValue;
			}
		}
	} else if (xml.nodeType === 3) {
		// text
		obj = xml.nodeValue;
	}

	// do children
	// If all text nodes inside, get concatenated text from them.
	let textNodes = [].slice.call(xml.childNodes).filter(function (node) {
		return node.nodeType === 3;
	});

	if (xml.hasChildNodes() && xml.childNodes.length === textNodes.length) {
		obj = [].slice.call(xml.childNodes).reduce(function (text, node) {
			return text + node.nodeValue;
		}, '');
	} else if (xml.hasChildNodes()) {
		for (let i = 0; i < xml.childNodes.length; i++) {
			let item = xml.childNodes.item(i);
			let nodeName = item.nodeName;
			if (nodeName.indexOf(':') > -1)
				nodeName = nodeName.split(':').slice(-1);
			if (typeof obj[nodeName] == 'undefined') {
				obj[nodeName] = xmlToJson(item);
			} else {
				if (typeof obj[nodeName].push == 'undefined') {
					let old = obj[nodeName];
					obj[nodeName] = [];
					obj[nodeName].push(old);
				}
				obj[nodeName].push(xmlToJson(item));
			}
		}
	}
	return obj;
}

function uuid() {
	let chars = '0123456789abcdef'.split('');

	let uuid = [], rnd = Math.random, r;
	uuid[8] = uuid[13] = uuid[18] = uuid[23] = '-';
	uuid[14] = '4'; // version 4

	for (let i = 0; i < 36; i++) {
		if (!uuid[i]) {
			r = 0 | rnd() * 16;

			uuid[i] = chars[(i == 19) ? (r & 0x3) | 0x8 : r & 0xf];
		}
	}

	return uuid.join('');
}

function postQuoteApi(method, data) {
	return postMethod(`postQuoteApi?method=${method}`, {
		data: JSON.stringify(data),
		headers: {
			quoteApiUrl: Auth.quoteApiUrl()
		}
	});
}

function getQuoteApi(method, parameters, pageSize) {
	return callMethod('quoteApi', { method, pageSize: pageSize || unlimited, parameters }, {
		headers: {
			quoteApiUrl: Auth.quoteApiUrl()
		}
	});
}

function postExternalApi(method, data, config, additionalOpts) {
	if (Auth.isLocal) return Promise.resolve(postLocalFakeData(method, data));
	let forceTokenAuth = additionalOpts && additionalOpts.forceTokenAuth ? '&forceTokenAuth=true' : '';
	if (__DEBUG__)
		console.debug(`Post ${method}`, { data });
	return postMethod(`postExternalApi?method=${method}${forceTokenAuth}`, {
		data: JSON.stringify(data),
		...(config || {})
	});
}

function getExternalApi(method, parameters, pageSize, useCache, config, additionalOpts) {
	let id = `getExternalApi_${method}_${parameters}_${pageSize}`;
	if (__DEBUG__)
		console.debug('Get external api', { method, parameters, pageSize, useCache, config, additionalOpts });
	if (Auth.isLocal) return Promise.resolve(getLocalFakeData(method, parameters));
	if (useCache && promiseCache[id]) {
		if (__DEBUG__)
			console.debug('Cache used for', id);
		return promiseCache[id];
	}
	let promise = callMethod('externalApi', { method, pageSize: pageSize || unlimited, parameters, forceTokenAuth: additionalOpts && additionalOpts.forceTokenAuth }, config);
	if (useCache) {
		promiseCache[id] = promise;
		promise.catch(() => promiseCache[id] = null);
	}

	return promise;
}

function getLocalFakeData(method, parameters) {
	const fakeObject = {
		Oid: 'fa10ad11-c63a-46e2-9f31-a79a76849444',
		Name: 'Fake test'
	}

	const fakeResultsRespond = { Results: [fakeObject] };

	const fakeCreative = {
		...fakeObject,
		JsonConfig: {
			templateFields: [
				{ id: 'test1', description: 'Company name', default: 'Test company' },
				{ id: 'test2', description: 'Slogan', default: 'Drive your dream' }
			]
		},
		IsTemplate: true
	}

	const fakeInsertion = {
		...fakeObject,
		Name: 'Test fake',
		Text: '',
		Ssml: '<speak xmlns="http://www.w3.org/2001/10/synthesis" xmlns:mstts="http://www.w3.org/2001/mstts" xmlns:emo="http://www.w3.org/2009/10/emotionml" version="1.0" xml:lang="en-US"><voice name="en-US-JennyNeural"><mstts:silence type="Leading-exact" value="0"></mstts:silence><prosody rate="1" pitch="+0%">Test1 Test2 Test3 <adthos:audio class="dynamic highlighted" id="fa10ad11-c63a-46e2-9f31-a79a76849444" outparam="null" defaulttext="" customvalues="[]">value</adthos:audio> Test4</prosody><mstts:silence type="Tailing-exact" value="0"></mstts:silence></voice></speak>',
		StartTime: 1000,
		StartOffset: 0,
		EndOffset: 0,
		Duration: 2000,
		Language: 'en-US',
		Voice: 'en-US-JennyNeural',
		VoiceStyle: null,
		Rate: 1,
		Pitch: 1,
		CreativeId: fakeObject.Oid,
		SubInsertions: [],
		Type: 'SsmlInsertion',
		type: 'textToSpeech',
		JsonConfig: {
			trackNumber: 0
		}
	}

	switch (method) {
		case 'Localization/GetLocals':
		case 'Localization/GetLocalizedMessages':
			return { Results: [] };
		case 'CreativeRevise/GetAllRevisions':
		case 'Voice/GetVoicesList':
			return [];
		case 'creative/GetCreative':
			return fakeCreative;
		case 'creative/GetSsmlInsertion':
			return fakeInsertion;
		case 'creative/GetInsertionsByCreative':
			return { Results: [fakeInsertion] };
		default:
			if (method.endsWith('s'))
				return fakeResultsRespond;
			return {
				...fakeObject,
				Results: [fakeObject]
			};
	}
}

function postLocalFakeData(method, data) {
	switch (method) {
		default:
			if (!data.Oid) {
				data.Oid = uuid();
			}
			return data;
	}
}

function postAudioApi(method, data, config) {
	const apiUrl = getAudioApiUrl();
	const callUrl = `${apiUrl}${method}?environment=${Auth.environment()}`;
	if (__DEBUG__)
		console.debug('Post audio api', { callUrl, method, data, config });
	return axios.post(callUrl, data, {
		...(config || {}),
		headers: {
			authToken: Auth.accessToken(),
			AdthosApiUrl: Auth.userInfo?.user?.DefaultApiUrl,
			Authorization: `Bearer ${Auth.accessToken()}` ,
			...__HEADERS__,
			...(config?.headers || {})
		}
	}).then(r => r.data);
}

function getAudioApi(method, parameters, pageSize, useCache, config, additionalOpts) {
	const apiUrl = getAudioApiUrl();
	const callUrl = `${apiUrl}${method}?environment=${Auth.environment()}&${parameters}`;
	if (__DEBUG__)
		console.debug('Post audio api', { callUrl, method, parameters, config });
	return axios.get(callUrl, config);
}

function getAudioApiUrl() {
	let apiUrl = Auth.audioApiUrl();
	apiUrl += apiUrl.substr(-1) === '/' ? '' : '/';
	return apiUrl;
}

function getInstructionsByCampaign(campaignId) {
	return getExternalApi('Instruction/GetInstructionsByCampaign', `campaignId=${campaignId}`)
		.then(r => {
			if (__DEBUG__)
				console.debug('Instructions loaded', { r });
			return r.Results;
		});
}

function getRadioData(pageIndex, limit, opts, markets, formats) {
	if (!markets) markets = [];
	if (!formats) formats = [];
	return getCartoSql(`
		select ST_Buffer(ST_SetSRID(ST_Point(longtitude::decimal,latitude::decimal),4326)::geography, radius::decimal)::geometry as geom, rad.*, CONCAT(rad.station, ', ', rad.slogan) as fullname
		from us_radio rad
		where radius not in ('#N/A')
			${markets.length ? `AND nielsen_dma in (${markets.map(f => `'${f}'`).join(',')})` : ''}
			${formats.length ? `AND primary_format in (${formats.map(f => `'${f}'`).join(',')})` : ''}
			${opts.where ? `AND (${opts.where})` : ''}
			${opts.byKey ? `AND ("station" = '${opts.byKey}')` : ''}
		limit ${limit} offset ${pageIndex * limit}
	`).then(r => ({ items: r.map(f => ({ ...f, id: f.quote_station_id })) }));
}

function getMarkets(pageIndex, limit, opts) {
	return getCartoSql(`
		select *
		from (
			SELECT distinct(nielsen_dma) as name
			FROM us_radio
			where nielsen_dma != ''
		) data
		where name is not null
			${opts.where ? `AND (${opts.where})` : ''}
			${opts.byKey ? `AND ("name" = '${opts.byKey}')` : ''}
		ORDER BY NAME
		limit ${limit} offset ${pageIndex * limit}
	`).then(r => {
		let markets = r.map(f => ({ ...f, id: f.name })).filter(f => f.name);
		if (__DEBUG__)
			console.info('Markets loaded', { markets });
		return markets;
	});
}

function getFormats(markets, pageIndex, limit, opts) {
	if (!markets) markets = [];
	return getCartoSql(`
		select *
		from (
			SELECT distinct(primary_format) as name
			FROM us_radio
			WHERE primary_format != ''
				${markets.length ? `AND nielsen_dma in (${markets.map(f => `'${f}'`).join(',')})` : ''}
		) data
		WHERE name is not null			
			${opts.where ? `AND (${opts.where})` : ''}
			${opts.byKey ? `AND ("name" = '${opts.byKey}')` : ''}
		ORDER BY NAME
		limit ${limit} offset ${pageIndex * limit}
	`).then(r => {
		let formats = r.map(f => ({ ...f, id: f.name })).filter(f => f.name);
		if (__DEBUG__)
			console.info('Formats loaded', { formats });
		return formats;
	});
}

function getRules(pageIndex, limit, opts, plain) {
	if (opts && opts.byKey)
		return getExternalApi('EventRule/GetEventRulesByIds', `eventRulesIds=${opts.byKey}`, limit).then(r => plain ? { Results: r } : r);
	return getExternalApi('EventRule/GetEventRules', `page=${pageIndex + 1}&searchByName=${opts && opts.filter && opts.filter.filterValue || ''}`, limit).then(r => plain ? r : r.Results);
}

function getSpeechVoices(locale) {
	return getExternalApi('Voice/GetVoicesList', locale ? `language=${locale}` : null)
		.then(r => {
			if (__DEBUG__)
				console.debug('Voices loaded', { r });
			return r;
		});
}

function getVoiceDetails(voiceId) {
	return getExternalApi('Voice/GetVoiceDetails', `voiceId=${voiceId}`);
}

function getExternalService(id, force) {
	return force ? 
		getExternalApi('ExternalService/GetExternalService', `oid=${id}`)
			.then(r => 
				getExternalServices(0, 1, { byKey: id }).then(e => {
					if (e?.Results[0]) {
						_.assign(e?.Results[0], processExternalService(r));
						return e?.Results[0];
					} else {
						processExternalService(r)
						e.DataSource.push(r);
						return r;
					}
				}))
		: getExternalServices(0, 1, { byKey: id }).then(r => r?.Results[0]);
}

function processExternalService(service) {
	[
		...(service.JsonDefinition.Parameters),
		...(service.JsonDefinition.ComplexParameters || [])
	]
		.map(p => {
			Object.defineProperty(p, '__service', {
				get: () => service
			})
		});
	return service;
}

let externalServicesPromise = null;
function getExternalServices(pageIndex, limit, opts) {
	if (!externalServicesPromise)
		externalServicesPromise = getExternalApi('ExternalService/GetExternalServices', 'returnJsonDefinition=true&activeOnly=true', unlimited)
			.then(r => {
				r.Results.map(service => processExternalService(service));
				return r;
			});

	if (opts && opts.byKey) {
		if (__DEBUG__)
			console.debug('Load by key external service', opts);
		return externalServicesPromise.then(r => ({ Results: [r.Results.find(f => f.Oid === opts.byKey || f.Code === opts.byKey)], DataSource: r.Results }));
	}
	const searchValue = opts && opts.filter && opts.filter.filterValue || '';
	return externalServicesPromise.then(r => {
		const results = _.orderBy(r.Results.filter(f => (opts?.baseServices || !f.IsBaseService) && (!searchValue || `${f.Name}`.toLowerCase().indexOf(searchValue.toLowerCase()) > -1)), ['Name']);
		return {
			TotalNumberOfRecords: results.length,
			Results: results.slice(pageIndex * limit, (pageIndex + 1) * limit),
			DataSource: r.Results
		}
	});
}

async function getOwnExternalServices(pageIndex, limit, opts) {
	if (!externalServicesPromise)
		externalServicesPromise = getExternalApi('ExternalService/GetExternalServices', 'returnJsonDefinition=true&activeOnly=true', unlimited)
			.then(r => {
				r.Results.map(service => processExternalService(service));
				return r;
			});

	const r = await externalServicesPromise.then(r => {
		const items = r.Results.filter(f => f.OwnerOid === Auth.companyId() || Auth.isAdmin() || (f.OwnerOid === null && Auth.canEditPublicObjects()));
		return {
			Results: items,
			TotalNumberOfRecords: items.length
		}
	});

	if (opts && opts.byKey) {
		if (__DEBUG__)
			console.debug('Load by key external service', opts);
		return externalServicesPromise.then(r => ({ Results: [r.Results.find(f => f.Oid === opts.byKey)], DataSource: r.Results }));
	}

	if (opts?.filter?.filterValue) {
		r.Results = r.Results.filter(f => `${f.Name}`.toLowerCase().indexOf(opts?.filter?.filterValue.toLowerCase()) > -1
			|| `${f.ServiceCategory?.Name}`.toLowerCase().indexOf(opts?.filter?.filterValue.toLowerCase()) > -1);
		r.TotalNumberOfRecords = r.Results?.length;
	}

	return r;
}

let webServiceCategoriesPromise = null;
function getWebServiceCategories(pageIndex, limit, opts) {
	if (!webServiceCategoriesPromise)
		webServiceCategoriesPromise = getExternalApi('WebServiceCategory/GetWebServiceCategories', '', unlimited);

	if (opts && opts.byKey) {
		return webServiceCategoriesPromise.then(r => ({ Results: [r.Results.find(f => f.Oid === opts.byKey)] }));
	}
	const searchValue = opts && opts.filter && opts.filter.filterValue || '';
	return webServiceCategoriesPromise.then(r => {
		const results = r.Results.filter(f => !searchValue || `${f.Name}`.toLowerCase().indexOf(searchValue.toLowerCase()) > -1);
		return {
			TotalNumberOfRecords: results.length,
			Results: results.slice(pageIndex * limit, (pageIndex + 1) * limit)
		}
	});
}

function getUtcDate(date) {
	return `${moment(date).utc().format('YYYY-MM-DDTHH:mm:ss.SSSZ')}`;
}

function dateToString(date) {
	return `${moment(date).format('YYYY-MM-DDTHH:mm:ss.SSS')}`;
}

function externalServiceGetParamValue(param, value) {
	if (!value) return;
	if (param.DataType === 'date') {
		const date = new Date(value);
		if (isNaN(date)) return value;
		return dateToString(value);
	}
	return value;
}

function getExternalCompanies(pageIndex, limit, opts, countryId) {
	let forceTokenAuth = opts.forceTokenAuth !== false && !(opts && opts.byKey);
	if (opts && opts.byKey)
		return getExternalApi('Company/GetCompany', `companyId=${opts.byKey}`, void 0, void 0, void 0, { forceTokenAuth }).then(r => ({ Results: [r] }));
	let onlyUnregistered = opts && opts.onlyUnregistered ? '&unregisteredOwnersOnly=true' : '';
	if (opts && opts.byType !== void 0)
		return getExternalApi('Company/GetCompaniesByType', `page=${pageIndex + 1}&searchByName=${opts && opts.filter && opts.filter.filterValue || ''}${onlyUnregistered}&companyType=${opts.byType}&countryId=${countryId || ''}`, limit, void 0, void 0, { forceTokenAuth });
	return getExternalApi('Company/GetCompanies', `page=${pageIndex + 1}&searchByName=${opts && opts.filter && opts.filter.filterValue || ''}${onlyUnregistered}&countryId=${countryId || ''}`, limit, void 0, void 0, { forceTokenAuth });
}

function getAllCreatives(pageIndex, limit, opts) {
	if (opts && opts.byKey)
		return getExternalApi('Creative/GetCreative', `creativeId=${opts.byKey}`).then(r => ({ Results: [r] }));

	let filter = opts?.filter;
	if (opts.onlyPublished) {
		filter = [
			['IsPublished', '=', 'true'],
			...(filter ? ['and', filter] : [])
		]	
	}
	let filterCriteria = `${filter && filter.length && `&filterCriteria=${encodeURIComponent(JSON.stringify(filter))}` || ''}`;
	let sorting = opts.sort && `&sorting=${encodeURIComponent(JSON.stringify(opts.sort))}` || '';
	return getExternalApi(
		'Creative/GetAllCreatives',
		`page=${pageIndex + 1}${filterCriteria}${sorting}`,
		limit
	);
}

function getCreativeTemplates(pageIndex, limit, opts, onlyMyTemplates = false) {
	let filterCriteria = `${opts && opts.filter && opts.filter.length && `&filterCriteria=${encodeURIComponent(JSON.stringify(opts.filter))}` || ''}`;
	let sorting = opts.sort && `&sorting=${encodeURIComponent(JSON.stringify(opts.sort))}` || '';
	return getExternalApi(
		'Creative/GetCreativeTemplates',
		`selectOnlyMyTemplates=${onlyMyTemplates}&page=${pageIndex + 1}${filterCriteria}${sorting}`,
		limit
	);
}

function getCurrencies(page, searchByName = '') {
	return getExternalApi(
		'Service/GetCurrencies',
		`page=${page}&searchByName=${searchByName}`
	);
}

function getLanguages(pageIndex, limit, opts) {
	if (opts?.byKey)
		return getExternalApi('Language/List', `page=${pageIndex}`, unlimited, true).then(r => ({		
			Results: r.Results.filter(f => f.Code === opts.byKey)
		}));
	return getExternalApi('Language/List', `page=${pageIndex}`, unlimited, true).then(r => {
		if (opts?.filter?.filterValue) {
			let Results = r.Results.filter(f => `${f.Name}`.toLowerCase().indexOf(`${opts?.filter?.filterValue}`.toLowerCase()) > -1);
			return {
				Results,
				TotalNumberOfRecords: Results.length
			}
		}
		return r;
	});
}

function getDynamicDatasets(pageIndex, limit, opts) {
	if (opts && opts.byKey)
		return getExternalApi('DynamicDataset/GetDynamicDataset', `datasetId=${opts.byKey}`).then(r => ({ Results: [r] }));
	return getExternalApi('DynamicDataset/GetDynamicDatasets', `page=${pageIndex + 1}`, limit);
}

function getExternalStations(pageIndex, limit, opts) {
	if (opts && opts.byKey)
		return getExternalApi('Station/GetStationsByIds', `stationIds=${opts.byKey}`).then(r => ({ Results: r }));
	let countryFilter = !opts?.allCountries ? `countryId=${Auth.countryId()}&` : '';
	let licensedFilter = opts?.onlyLicensed ? `onlyLicensed=true&` : '';
	let needFilterByUser = opts?.allStations ? 'needFilterByUser=false&' : '';
	let polygonFilter = opts?.polygonWkt ? `polygonWkt=${opts?.polygonWkt}&` : '';
	let stationsFilter = `needFilterByUser=${opts?.onlyMyStations ? 'true' : 'false'}&`;
	let unlinkedOnlyFilter = opts?.unlinkedOnly ? `unlinkedOnly=true&` : '';
	return getExternalApi('Station/GetStations', `${countryFilter}${licensedFilter}${needFilterByUser}${polygonFilter}${stationsFilter}${unlinkedOnlyFilter}page=${pageIndex + 1}&searchByName=${opts && opts.filter && opts.filter.filterValue || ''}`, limit);
}

function getExternalCompanyStations(pageIndex, limit, opts, companyId) {
	if (opts && opts.byKey)
		return getExternalApi('Station/GetStationsByIds', `stationIds=${opts.byKey}`).then(r => ({ Results: r }));
	let searchByName = opts && opts.filter && opts.filter.filterValue || '';
	if (searchByName)
		searchByName = `&searchByName=${searchByName}`
	return getExternalApi('Company/GetCompanyStations', `companyId=${companyId || Auth.companyId()}&page=${pageIndex + 1}${searchByName}`, limit);
}

function getExternalNetworks(pageIndex, limit, opts) {
	if (opts && opts.byKey)
		return getExternalApi('Station/GetNetworksByIds', `networkIds=${opts.byKey}`).then(r => ({ Results: r }));
	return getExternalApi('Station/GetNetworks', `page=${pageIndex + 1}&searchByName=${opts && opts.filter && opts.filter.filterValue || ''}`, limit);
}

function getExternalCountries(pageIndex, limit, opts) {
	if (opts && opts.byKey)
		return getExternalApi('Service/GetCountriesByIds', `countryIds=${opts.byKey}`).then(r => ({ Results: r }));
	return getExternalApi('Service/GetCountries', `page=${pageIndex + 1}&searchByName=${opts && opts.filter && opts.filter.filterValue || ''}`, limit);
}

function getExternalCountryRegions(pageIndex, limit, opts, countryId) {
	if (opts && opts.byKey)
		return getExternalApi('Service/GetRegionsByIds', `regionIds=${opts.byKey}`).then(r => ({ Results: r }));
	return getExternalApi('Service/GetRegionsByCountry', `page=${pageIndex + 1}&countryId=${countryId}&searchByName=${opts && opts.filter && opts.filter.filterValue || ''}`, limit);
}

function getExternalZones(pageIndex, limit, opts, zoneType) {
	zoneType = zoneType || 0;
	if (opts && opts.byKey) {
		if (__DEBUG__)
			console.debug('Get external zone', { opts, zoneType });
		return getExternalApi('Station/GetZonesByIds', `zoneIds=${opts.byKey}`).then(r => ({ Results: r }));
	}
	return getExternalApi('Station/GetZones', `page=${pageIndex + 1}&searchByName=${opts && opts.filter && opts.filter.filterValue || ''}&zoneType=${zoneType}`, limit);
}

function getExternalStationFormats(pageIndex, limit, opts) {
	if (opts && opts.byKey)
		return getExternalApi('Station/GetFormatsByIds', `formatIds=${opts.byKey}`).then(r => ({ Results: r }));
	return getExternalApi('Station/GetFormats', `page=${pageIndex + 1}&searchByName=${opts && opts.filter && opts.filter.filterValue || ''}`, limit);
}

function getCurrentUserProfile() {
	return getExternalApi('Security/GetCurrentUser');
}

function getCurrentUserPhoto() {
	return getMethodUrl(`externalApi?accessToken=${Auth.accessToken()}&method=Security/GetUserPhoto&parameters=${encodeURIComponent(`userId=${Auth.userId()}`)}`);
}

function getCompanyContacts(pageIndex, limit, opts, companyId, statusId) {
	if (statusId)
		return getExternalApi('Company/GetCompanyContactsByRegistrationStatus', `page=${pageIndex + 1}&companyId=${companyId}&status=${statusId}`);
	return getExternalApi('Company/GetCompanyContacts', `page=${pageIndex + 1}&companyId=${companyId}`);
}

function getLicenseRequests(pageIndex, limit, opts, state) {
	return getExternalApi('Company/GetLicenseRequests', `page=${pageIndex + 1}&state=${state || -1}&searchByName=${opts && opts.filter && opts.filter.filterValue || ''}`, limit);
}

function getBrowserLocationByIp() {
	if (__DEBUG__)
		console.debug('getBrowserLocationByIp');
	return axios.get('https://ipgeolocation.abstractapi.com/v1/?api_key=f395b791ac0c446daab6651d910543fb').then(r => {
		if (__DEBUG__)
			console.debug('Browser location loaded', { r });
		return r.data;
	});
}

function getStationStreaminEncoders(stationId, pageIndex, limit, opts) {
	return getExternalApi('station/GetStationEncoders', `stationId=${stationId}&page=${pageIndex + 1}`, limit);
}

function getInvoices(pageIndex, limit, opts) {
	let searchString = `${opts && opts.searchString && `&searchString=${encodeURIComponent(opts.searchString)}` || ''}`;
	let filterCriteria = `${opts && opts.filter && opts.filter.length && `&filterCriteria=${encodeURIComponent(JSON.stringify(opts.filter))}` || ''}`;
	let companyFilter = Auth.isStationOwner() ? 'stationOwnerId' : (Auth.isAgency() ? 'agencyId' : 'advertiserId');
	return getExternalApi(`ElectronicInvoicing/GetInvoices`, `page=${pageIndex + 1}&${companyFilter}=${Auth.companyId()}${searchString}${filterCriteria}`, limit);
}

function getInvoiceUploadings(pageIndex, limit, opts) {
	return getExternalApi('ElectronicInvoicing/GetInvoiceUploadings', `page=${pageIndex + 1}&stationOwnerId=${Auth.companyId()}`, limit);
}

function getInvoicesLog(uploadingId, recordCode, pageIndex, limit, opts) {
	return getExternalApi('ElectronicInvoicing/GetInvoiceLog', `page=${pageIndex + 1}&uploadingId=${uploadingId || ''}&recordCode=${recordCode || ''}`, limit);
}

function getCustomerInvoices(pageIndex, limit, opts) {
	return getExternalApi(`CustomerInvoice/GetInvoices`, `page=${pageIndex + 1}`, limit);
}

function downloadCustomerInvoice(id) {
	return getExternalApi(`CustomerInvoice/DownloadPdfInvoice?invoiceId=${id}`, {}, void 0, false, {
		responseType: 'blob'
	});
}

function getAzureStationLog(stationId, dateTime) {
	return new Promise((mainResolve, mainReject) =>
		getAzureStationLogConfig(stationId).then(r =>
			getExternalApi('station/GetLoggingPlan', `adthosChannelId=${stationId}`).then(sp => {
				let chunks = [];
				let date = moment(dateTime).utc();
				let datePart = date.format('YYYYMMDDHH');
				let chunkSize = (sp.ChunkSize || 30000) / 1000;

				let mainChunk = (date.minute() / chunkSize * 60) + 1;
				let maxChunk = 60 / chunkSize * 60;
				if (mainChunk > 1)
					chunks.push(`${datePart}-${`000${mainChunk - 1}`.substr(-3)}`);
				chunks.push(`${datePart}-${`000${mainChunk}`.substr(-3)}`);
				let lastChunk = mainChunk;
				if (mainChunk < maxChunk) {
					chunks.push(`${datePart}-${`000${mainChunk + 1}`.substr(-3)}`);
					lastChunk = mainChunk + 1;
				}

				if (__DEBUG__)
					console.debug('Get azure station log', { stationId, dateTime, mainChunk, chunks });

				if (r.Uri) {
					let uri = `${r.Uri}`.split('?');
					let part1 = uri[0];
					let part2 = uri[1];

					let loadChunk = (chunk, idx, chunkNumber) => {
						if (chunkNumber)
							chunk = `${datePart}-${`000${chunkNumber}`.substr(-3)}`;
						return new Promise((resolve, reject) => {
							let link = `${part1}/${chunk}.aac?${part2}`;
							console.info(`Loading audio chunk ${chunk}`);
							axios.get(link, { responseType: 'blob' }).then(r => {
								let loadedChunk = { blob: r.data, chunk, idx };
								if (__DEBUG__)
									console.debug('Chunk loaded', loadedChunk);
								resolve(loadedChunk);
								return r;
							}).catch((ex) => resolve(null));
						})
					}

					let promises = chunks.map((chunk, idx) => loadChunk(chunk, idx));

					Promise.all(promises).then(r => {
						let result = _.sortBy(r.filter(v => !!v), v => v.idx).map((v, idx) => ({ ...v, idx }));
						if (__DEBUG__)
							console.debug('GetAzureStationLog loaded data', { result, r });
						if (!result.length) {
							mainReject('There is no audio data');
							appConfig.notifyWarning(appConfig.getVueApp().$tCaption('There is no audio data'));
						}
						else
							mainResolve({
								data: result,
								chunkSize,
								maxChunk,
								mainChunk,
								lastChunk,
								date,
								uri,
								uriPart1: part1,
								uriPart2: part2,
								loadChunk
							});
					}).catch(ex => {
						if (__DEBUG__)
							console.error(ex);
						mainReject(ex)
					});
				}
				return null;
			})
		)
	);
}

const azureLogCachedSessions = {};
function getAzureStationLogConfig(stationId) {
	if (azureLogCachedSessions[stationId] && azureLogCachedSessions[stationId].date.diff(moment(), 'seconds') > 20) {
		if (__DEBUG__)
			console.debug('Returned azure config from cache');
		return azureLogCachedSessions[stationId].promise;
	}

	if (__DEBUG__)
		console.debug('New config request');

	let promise = getExternalApi('station/GetLoggingContainerSas', `stationId=${stationId}`).then(r => {
		if (r && r.Success !== false) {
			azureLogCachedSessions[stationId] = {
				date: moment(r.ExpiresOnUtc),
				promise
			};
			if (__DEBUG__)
				console.debug('Station log', { stationId, r });
		} else {
			azureLogCachedSessions[stationId] = null;
			appConfig.notifyError(r.ErrorMessage);
		}

		return r;
	});

	azureLogCachedSessions[stationId] = {
		date: moment().add('minutes', 10),
		promise
	};

	return promise;
}

function getAzureSettings() {
	return getExternalApi('Company/GetAzureSettings', '', 1, true);
}

function getReconciliationCampaigns(pageIndex, limit, opts) {
	return getExternalApi('Reconciliation/GetUnionCampaignsList', `page=${pageIndex + 1}&searchByName=${opts && opts.filter && opts.filter.filterValue || ''}`, limit);
}

function getMySubscriptions(pageIndex, limit, opts, status) {
	return getExternalApi('CustomerSubscription/GetMySubscriptions', `page=${pageIndex + 1}&status=${status || 0}`, limit);
}

function getSubscriptionPlans(pageIndex, limit, opts) {
	return getExternalApi('CustomerSubscription/GetSubscriptionPlans', `page=${pageIndex + 1}`, limit, true);
}

function getStripeKey() {
	return getExternalApi('PaymentSystem/GetStripeApiPubKey', '', 1, true).then(r => r && r.PublicKey);
}

function clearCache() {
	objectsCache = {};
	promiseCache = {};
	multipleCallsCache = {};

	appConfig.eventsBus.$emit('clear-cache');
}

function getTranslations(params = {}) {
	params = Object.keys(params).map(key => `${key}=${params[key]}`).join('&');
	return getExternalApi('Localization/GetLocalizedMessages', params).then(r => {
		return r.Results.map(item => ({ localeId: item.LocaleOid, key: item.Key, translation: item.Translation }));
	});
}

function addLocale(data) {
	return postExternalApi('Localization/CreateUpdateLocale', { Code: data.code, Name: data.name });
}

function updateTranslations(data) {
	return postExternalApi('Localization/CreateUpdateLocalizedMessages', data);
}

function deleteTranslations(data) {
	return postExternalApi('Localization/DeleteLocalizedMessages', data);
}

function getAllLocales() {
	return getExternalApi(`Localization/GetLocals`).then(r => {
		return r.Results.map(locale => ({ id: locale.Oid, code: locale.Code, label: locale.Name, }))
	});
}

function validateEmail(email) {
	const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
	return re.test(String(email).toLowerCase());
}

function getCreativePublishHistory(pageIndex, limit, opts, creativeId) {
	return getExternalApi('PublicationSession/GetPublicationSessions', `creativeId=${creativeId}&page=${pageIndex + 1}`, limit);
}

function getGeoTargets(pageIndex, limit, opts, insertionId) {
	const insertionFilter = insertionId && `&insertionOid=${insertionId}` || '';
	return getExternalApi('Target/GetGeoTargets', `page=${pageIndex + 1}${insertionFilter}&searchByName=${opts && opts.filter && opts.filter.filterValue || ''}`, limit);
}

function readBinaryJSONResponse(blob) {
	if (!blob) return Promise.resolve(null);
	return new Promise(resolve => {
		let fr = new FileReader;
		fr.onloadend = () => {
			let b64DecodeUnicode = (str) => {
				return decodeURIComponent(atob(str).split('').map(function (c) {
					return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
				}).join(''));
			};

			if (blob?.type === 'application/json') {
				let jsonText = b64DecodeUnicode(`${fr.result}`.split(';base64,')[1]);
				try {
					resolve(JSON.parse(jsonText || 'null'));
				} catch {
					resolve(jsonText)
				}
			} else {
				resolve(null);
			}
		};
		fr.readAsDataURL(blob);

		return null;
	});
}

function cloneCreative(creativeId, name, navigate) {
	let resolve = null;
	let promise = new Promise(res => resolve = res);
	let app = appConfig.getVueApp();
	const clone = (copySubTracks) => {
		app.$notifyMessage(app.$tCaption('Making a copy of "<CREATIVE_NAME>", please wait..', '@creative.Detail').replace('<CREATIVE_NAME>', name), { busy: true, icon: 'clone' });
		getExternalApi('Creative/CopyCreative', `creativeId=${creativeId}&copySubTracks=${copySubTracks}`).then((r) => {
			app.$notifyMessage(app.$tCaption('Creative has been successfully copied', '@creative.Detail'), { busy: false });
			//Notification through signalR - processCreativeGenerationStatusChanged: DO NOT WORK ANYMORE
			app.$confirmMessage({
				title: app.$tCaption('Creative generated', '@actions'),
				text: app.$tCaption('The creative "<NAME>" has been successfully copied. Do you want to open it?').replace(/<NAME>/g, name),
				handler: () => {
					app.$router.push({ path: `/creative/Detail/${r?.Oid}` });
				}
			});
			return r;
		}).finally(() => resolve());
	}

	app.$confirmMessage({
		title: app.$tCaption('Copy creative', 'creative'),
		message: app.$tCaption('Do you want to copy sub-tracks?', 'creative'),
		buttons: [
			{
				text: app.$tCaption('Yes', '@actions'),
				onClick: () => clone(true)
			},
			{
				text: app.$tCaption('No', '@actions'),
				onClick: () => clone(false)
			},
			{
				text: app.$tCaption('Cancel', '@actions'),
				onClick: () => resolve(),
				type: 'normal'
			}
		]
	});
	
	return promise;
}

function getTargetProjects(pageIndex, limit, opts) {
	if (opts && opts.byKey)
		return getExternalApi('TargetProject/GetTargetProjectsByIds', `targetProjectIds=${opts.byKey}`).then(r => ({ Results: r }));
	return getExternalApi('TargetProject/GetTargetProjects', `page=${pageIndex + 1}&searchByName=${opts && opts.filter && opts.filter.filterValue || ''}`, limit);
}

function getUsedLanguages(pageIndex, limit, opts, templatesOnly) {
	if (opts?.byKey) {
		const languageNames = new Intl.DisplayNames(['en'], { type: 'language', languageDisplay: 'standard' });
		return Promise.resolve({ 
			Results: [{
				Name: languageNames.of(opts?.byKey || 'en'),
				Code: opts.byKey
			}]
		});
	}
	return getExternalApi('Service/GetUsedLanguages', `templateOnly=${!!templatesOnly}`, unlimited).then(r => {
		return {
			Results: r.slice(pageIndex * limit, (pageIndex + 1) * limit),
			TotalNumberOfRecords: r.length
		}
	});
}

function getLanguageNameByCode(code) {
	const languageNames = new Intl.DisplayNames(['en'], { type: 'language', languageDisplay: 'standard' });
	return languageNames.of(code || 'en');
}

function getTargetProjectLinkedGeoTargets(pageIndex, limit, opts, targetProjectId) {
	return getExternalApi('TargetProject/GetLinkedGeoTargets', `page=${pageIndex + 1}&searchByName=${opts && opts.filter && opts.filter.filterValue || ''}&targetProjectId=${targetProjectId}`, limit);
}

function getActiveCompanyInvites(pageIndex, limit, opts, companyType) {
	return getExternalApi('Company/GetActiveInvites', `page=${pageIndex + 1}&companyType=${companyType || ''}`, limit);
}

function getRevisionsFromString(revisionsString) {
	if (!revisionsString) return null;
	return `${revisionsString || ''}`.split(',').map(f => {
		let item = `${f || ''}`.split(':');
		if (item?.length === 2) {
			return {
				languageCode: `${item[0]}`.trim(),
				creativeId: `${item[1]}`.trim()
			}
		}
	}).filter(f => f);
}

let timeZonesPromise = null;
function getTimeZones(pageIndex, limit, opts) {
	if (__DEBUG__)
		console.debug('Get time zones', { pageIndex, limit, opts });
	if (!timeZonesPromise) {
		timeZonesPromise = getExternalApi('Service/GetTimeZones', `page=${pageIndex + 1}`, 999).then(r => ({ Results: r }));
	}
	if (opts?.byKey)
		return timeZonesPromise.then(r => ({ Results: r.Results.filter(f => f.Id === opts?.byKey), TotalNumberOfRecords: 1 }));
	return timeZonesPromise.then(r => ({ 
			Results: r.Results
				.filter(f => opts?.filter && opts?.filter[2] ? f.DisplayName.toLowerCase().indexOf(opts?.filter[2].toLowerCase()) > -1 : true)
				.slice(pageIndex * limit, (pageIndex + 1) * limit)
				.map(f => ({ ...f, Time: (`${f.DisplayName}`.split(')')[0] || '').replace(/[()]/g, '').replace(/UTC/g, '') })),
			TotalNumberOfRecords: r.Results.length
	}));
}

async function getTimeZonesTimes(pageIndex, limit, opts) {
	const allTimeZones = await getTimeZones(0, 9999);
	const uniqueZones = _.uniqBy(allTimeZones.Results.filter(f => f.Time), f => f.Time);
	if (opts?.byKey)
		return allTimeZones.then(r => ({ Results: r.Results.filter(f => f.Time === opts?.byKey || f.Id === opts?.byKey), TotalNumberOfRecords: 1 }));
	return {
		Results: uniqueZones
			.filter(f => opts?.filter && opts?.filter[2] ? f.DisplayName.toLowerCase().indexOf(opts?.filter[2].toLowerCase()) > -1 : true)
			.slice(pageIndex * limit, (pageIndex + 1) * limit),
		TotalNumberOfRecords: uniqueZones.length
	}
}

function loadLexicons(pageIndex, limit, opts) {
	if (opts?.byKey) {
		return getExternalApi('Lexicon/GetLexicon', `id=${opts?.byKey}`).then(r => ({ Results: [r] }));
	}
	return getExternalApi('Lexicon/GetAllLexicons', `companyId=${Auth.companyId()}&page=${pageIndex + 1}&lexiconType=0`, limit)
}

function loadCustomTNs(pageIndex, limit, opts) {
	if (opts?.byKey) {
		return getExternalApi('Lexicon/GetLexicon', `id=${opts?.byKey}`).then(r => ({ Results: [r] }));
	}
	return getExternalApi('Lexicon/GetAllLexicons', `companyId=${Auth.companyId()}&page=${pageIndex + 1}&lexiconType=1`, limit)
}

function loadStylizers(pageIndex, limit, opts) {
	if (opts?.byKey) {
		return getExternalApi('SsmlStylizer/Get', `id=${opts?.byKey}`).then(r => ({ Results: [r] }));
	}
	return getExternalApi('SsmlStylizer/List', '', limit)
}

function getTemplateCategories(pageIndex, limit, opts) {
	if (opts?.byKey) {
		return getExternalApi('TemplateCategory/GetTemplateCategory', `id=${opts?.byKey}`).then(r => ({ Results: [r] }));
	}
	return getExternalApi('TemplateCategory/GetAllTemplateCategories', '', limit)
}

let langmapItemsData = null;
function langmapItems() {
	if (!langmapItemsData) {
		langmapItemsData = Object.keys(langmap).reduce((arr, f) => {
			if (`${f}`.indexOf('-') > -1) {
				let item = langmap[f];
				arr.push({
					locale: f,
					...item,
					displayName: item.englishName === item.nativeName ? item.nativeName : `${item.englishName} (${item.nativeName})`
				});
			}
			return arr;
		}, []);
	}
	return langmapItemsData;
}

function getHash(data) {
	var hash = 0, i, chr;
	var str = typeof (data) === 'object' ? JSON.stringify(data) : `${data}`;
	if (!str?.length) return hash;
	for (i = 0; i < str.length; i++) {
		chr = str.charCodeAt(i);
		hash = ((hash << 5) - hash) + chr;
		hash |= 0; // Convert to 32bit integer
	}
	return hash;
}

function safe(func) {
	if (func)
		try {
			func();
		} catch (ex) {
			if (__DEBUG__)
				console.warn(`Safe execution failed: ${ex}, ${func}`);
		}
}

function blob2Base64(blob) {
	return new Promise(res => {
		var reader = new FileReader();
		reader.readAsDataURL(blob);
		reader.onloadend = function() {
			res(reader.result);
		}
	});
}

function base64toBlob(b64Data, contentType='', sliceSize=512) {
	const byteCharacters = atob(b64Data);
	const byteArrays = [];

	for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
		const slice = byteCharacters.slice(offset, offset + sliceSize);

		const byteNumbers = new Array(slice.length);
		for (let i = 0; i < slice.length; i++) {
			byteNumbers[i] = slice.charCodeAt(i);
		}

		const byteArray = new Uint8Array(byteNumbers);
		byteArrays.push(byteArray);
	}

	const blob = new Blob(byteArrays, {type: contentType});
	return blob;
}

function dataURItoBlob(dataURI) {
	var byteString = atob(dataURI.split(',')[1]);
	var mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];

	var ab = new ArrayBuffer(byteString.length);
	var ia = new Uint8Array(ab);

	for (var i = 0; i < byteString.length; i++) {
		ia[i] = byteString.charCodeAt(i);
	}

	var blob = new Blob([ab], {type: mimeString});
	return blob;
  }

function downloadURI(uri, name) {
	var link = document.createElement('a');
	link.download = name;
	link.href = uri;
	link.target = '_blank';
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
}

function safeStringify(v) {
	const objectsCollection = [];
	const replacer = (key, value) => {
		try {
			if (typeof(value) === 'object' && objectsCollection.find(f => f === value)) {
				return undefined;
			}
			objectsCollection.push(value);
			return value;
		} catch {
			return undefined;
		}
	}
	return JSON.stringify(v, replacer);
}

function formatStringAsKey(val) {
	val = `${val}`.split(' ').map(f => capitalize(f)).join('');
	return val.replace(/[\^\'\"\!\@\#\$\%^\&*\(\)\_ ;:\,\.\[\]\`\№]/g, '');
}

const generatedMethods = [];
function generateMethodCaller(methodName) {
	if (generatedMethods[methodName])
		return generatedMethods[methodName];
	const method = (pageIndex, limit, opts) => {
		let filterCriteria = `${opts && opts.filter && opts.filter.length && `&filterCriteria=${encodeURIComponent(JSON.stringify(opts.filter))}` || ''}`;
		let sorting = opts.sort && `&sorting=${encodeURIComponent(JSON.stringify(opts.sort))}` || '';
		return getExternalApi(method, `page=${pageIndex + 1}${filterCriteria}${sorting}`, limit);
	}

	generatedMethods[methodName] = method;
	return method;
}

const objectTypes = {
	Station: 0,
	User: 1,
	Zone: 2,
	Company: 3,
	BaseInsertion: 4,
	Voice: 5,
	NetworkShow: 6,
	NetworkShowTalent: 7,
	Creative: 8,
	ExternalServiceDefinition: 9,
	CreativeTemplate: 10,
	Podcast: 11,
	Streaming: 12,
	Campaign: 13,
	WebServiceCategory: 14,
	Banner: 15,
	CreativeStep: 16,
	PublicationSchedule: 17,
	NetworkShowCategory: 18,
	NetworkShowClock: 19,
	NetworkShowCue: 20,
	NetworkShowEpisode: 21,
	NetworkShowLicense: 22,
	NetworkShowSchedule: 23,
	//24 is removed, next one will be 24
	TargetProject: 25,
	AIScriptGenerator: 26,
	AIEdit: 27,
	RuntimeJavaScript: 28,
	AIPostProcessingScript: 29,
	BriefBranding: 30,
	PersistentSchedule: 31,
	PersistentJobEntity: 32,
	CreativeMediaFileVariant: 33,
	PublicationSession: 34,
	OpenAiGpt3Log: 35,
	AiMusic: 36
}

function capitalize(str) {
	str = str || '';
	return str.charAt(0).toUpperCase() + str.slice(1);
}

function idToReadable(str) {
	if (!str) return '';
	let result = '';
	let beforeIsLower = false;
	for(var i = 0; i < str.length; i++) {
		if (str[i].match(/[ _.+!=]/)) {
			result += ' ';
		} else {
			if (str[i] === str[i].toUpperCase() && beforeIsLower && isNaN(str[i])) {
				result += ' ';
			}
			beforeIsLower = str[i] === str[i].toLowerCase();
			result += str[i];
		}
	}
	return capitalize(result);
}

const complexExternalServicesTypes =  { 
	geoplace: {
		default: ['Lon', 'Lat', 'PlaceName'],
		parameters: [
			{ name: 'Lon', dataType: 'number' }, 
			{ name: 'Lat', dataType: 'number' }, 
			{ name: 'PlaceName', dataType: 'string' },
			{ name: 'City', dataType: 'string' },
			{ name: 'State', dataType: 'string' },
			{ name: 'County', dataType: 'string' },
			{ name: 'Country', dataType: 'string' },
		],
		displayParameter: 'PlaceName',
		name: 'Geo-location'
	}
}

function getNextNaming(name, arrayOfExistingNames) {
	let index = 0;
	let currentName = name;

	while (arrayOfExistingNames.includes(currentName)) {
		index++;
		currentName = `${name}${index}`;
	}

	return currentName;
}

function externalServicesParametersDataTypes() {
	return ['string', 'date', 'number', 'boolean', 'list'];
}

function externalServicesOutputsDataTypes() {
	return ['string', 'date', 'number', 'boolean', 'list', 'range', 'media'];
}

function getFilterCriteriaSortingParams(opts) {
	if (!opts?.sort && opts?.defSorting) {
		if (opts) {
			opts.sort = opts.defSorting;
		} else {
			opts = { sort: opts.defSorting };
		}
	}

	let filterCriteria = opts?.filter?.length ? `&filterCriteria=${encodeURIComponent(JSON.stringify(opts.filter))}` : '';
	if (!filterCriteria && opts?.searchString) {
		filterCriteria = `&filterCriteria=${encodeURIComponent(JSON.stringify([opts?.searchField || 'Name', 'contains', opts?.searchString]))}`;
	}
	let sorting = opts?.sort ? `&sorting=${encodeURIComponent(JSON.stringify(opts.sort))}` : '';

	if (__DEBUG__)
		console.debug('getFilterCriteriaSortingParams', { opts, res: `${filterCriteria}${sorting}` });

	return `${filterCriteria}${sorting}`;
}

function linkUnlink(id, objectType, collectionFieldName, relatedObjectIds, isLink) {
	return postExternalApi('Service/LinkUnlink', {
		TargetObjectId: id,
		TargetObjectType: objectType,
		TargetObjectCollectionFieldName: collectionFieldName,
		RelatedObjectIds: relatedObjectIds,
		IsLinkAction: isLink
	});
}

function secondsToDisplayValue(seconds, opts) {
	const date = moment().startOf('day').add('seconds', opts?.preventRound ? seconds : Math.round(seconds));
	return date.format(seconds >= 60 * 60 ? (opts?.hFormat || 'HH:mm:ss') : (opts?.mFormat || 'mm:ss'));
}

function getArrayBufferFromBlob(blob) {
	return new Promise((res, rej) => {
		var fr = new FileReader();
		fr.onloadend = () => {
			res(fr.result);
		};
		fr.onerror = () => rej();
		fr.readAsArrayBuffer(blob);
	});
}

async function getAudioBufferFromBlob(blob, audioContext) {
	audioContext = audioContext || new AudioContext();
	const arrayBuffer = await getArrayBufferFromBlob(blob);
	return await new Promise((res, rej) => {
		audioContext.decodeAudioData(arrayBuffer, (buffer) => res(buffer), () => rej());
	});
}

function encodeHTML(text) {
	let textArea = document.createElement('textarea');
	textArea.innerText = text;
	let encodedOutput=textArea.innerHTML;
	let arr=encodedOutput.split('<br>');
	encodedOutput=arr.join('\n');
	return encodedOutput;
}

function decodeHTML(text) {
	let textArea = document.createElement('textarea');
	textArea.innerHTML = text;
	return textArea.value;
}

function htmlToText(html) {
	let div = document.createElement('div');
	div.innerHTML = html;
	return (div.textContent || div.innerText || '').replace(/[\u200B-\u200D\uFEFF]/gim, '');
}

function getBrowserInfo() {
	return axios.get('https://1.1.1.1/cdn-cgi/trace').then(r => {
		return Object.fromEntries(`${r.data}`.trim().split('\n').map(e => e.split('=')));
	});
}

function getPublicPath(origin) {
	const path = window.location.pathname.split('/');
	return (origin ? window.location.origin : '') + path.slice(0, path.length - 1).join('/');
}

async function sleep(time) {
	return new Promise(res => setTimeout(res, time));
}

function splitDataUri(dataUri) {
	let dataType = dataUri.split('data:')[1].split(';base64,')[0];
	let base64 = dataUri.split(';base64,')[1];

	return {
		dataType,
		base64
	}
}

async function grabMedia(mediaUrl, opts, asBlob) {
	if (__DEBUG__)
		console.debug('grabMedia', { mediaUrl, opts });
	const url = 'VideoGrabber/Execute';//Auth.userInfo.user.GrabVideoAzureFunctionUrl + '&name=RunCommand';
	const azureSettings = await getAzureSettings();
	const prepareAzureContainer = (res) => {
		if (!res) return null;
		return {
			StorageType: 2,
			AzureAccountOid: azureSettings.StorageAccount.Oid,
			AzureContainer: res?.azureFileInfo?.container,
			AzureFileUrl: `${azureSettings.StorageAccount.BlobEndPoint}${res?.azureFileInfo?.container}/${res?.azureFileInfo?.fileName}`,
			FileName: res?.azureFileInfo?.fileName,
			FileSize: res?.size
		}
	}

	let audio = null;
	let video = null;
	let title = null;

	const durableCall = postAudioApi(url, {
		VideoUrl: mediaUrl, //url for grabbing video
		GrabVideo: true, //either grab video stream or not
		GrabAudio: true, //either grab audio stream or not
		TargetResolutionHeight: __DEBUG__ && Auth.isLocalhost() && true ? 720 : 1080, //desired video resolution
		// It's command that allows to convert the grabbed audio stream in desired format with Ffmpeg command.
		// Bellow are examples of Ffmpeg commands:
		// to convert in wav: -i [input] [output.wav]
		// to convert in mp3: -i [input] -c:v copy -c:a libmp3lame -q:a 4 [output.mp3]
		ConvertAudioCommand: '-i [input] -c:v copy -c:a libmp3lame -q:a 4 [output.mp3]',
		SaveToAzure: !asBlob,
		SaveToAzureContainer: 'baseaudio',
		...(opts || {})
	});

	await durableCall.then(data => {
		if (!data) throw new Error(appConfig.getVueApp().$tCaption('Errors occured during operation.', '@Creative'));
		if (__DEBUG__)
			console.debug('grabMedia result', { data });

		const files = data?.outputFiles || [];
		const audioResult = files.find(f => f.mediaType === 1);
		const videoResult = files.find(f => f.mediaType === 2);

		audio = asBlob ? `data:audio/mp3;base64,${audioResult?.base64Content}` : prepareAzureContainer(audioResult);
		video = asBlob ? `data:video/mp4;base64,${videoResult?.base64Content}` : prepareAzureContainer(videoResult);
		return data;
	});

	return {
		audio,
		video,
		title
	}
}

async function denoiseMedia(opts) {
	let fileName = opts?.fileName;
	let base64 = opts?.base64;
	let azureFile = opts?.azureFile;
	let asBlob = opts?.asBlob || false;

	//const url = Auth.userInfo.user.DenoiserAzureFunctionUrl + '&name=RunCommand';

	let azureUrl = null;
	let azureBlob = null;
	let azureName = null;

	if (base64 || opts?.blob) {
		const base64Content = base64 && (await splitDataUri(base64))?.base64;
		const ext = 'wav';
		const blob = opts?.blob || await resampleBlob(await base64toBlob(base64Content, 'audio'), 48000);

		const name = `${fileName || ''}_${uuid()}.${ext}`;
		azureBlob = await uploadBlobWithInfoResponse(name, blob, 'temporary');
		
		const sas = (await getSasForAzureContainer('temporary', 300))?.sasKey;
		azureUrl = encodeURI(azureBlob?.AzureFileUrl) + `?${sas}`;

		azureName = `${fileName || ''}.${ext}`;
	} else if (azureFile) {
		const azureSettings = await getAzureSettings();
		const sas = (await getSasForAzureContainer(azureFile.AzureContainer, 300))?.sasKey;
		azureUrl = `${azureSettings.StorageAccount.BlobEndPoint}${azureFile?.AzureContainer}/${azureFile?.FileName}?${sas}`;
		azureName = azureFile?.FileName;
	}

	try {
		if (__DEBUG__)
			console.debug('Denoise azure', { azureUrl, azureName });

		let audio = null;
		const sasToContainer = (await getSasForAzureContainer(void 0, 300));

		let data = await postAudioApi('denoiser/execute', {
			"Command": `[${azureName}]`,
			"SasToContainer": sasToContainer?.sas,
			"InputFiles": [
				{
					"FileName": azureName,
					"URL": azureUrl
				}
			]
		});
		
		const outputLink = data?.outputFiles?.length && data.outputFiles[0];
		if (outputLink) {
			audio = await getBlob(outputLink, asBlob, { contentType: 'audio/wav' });
		}

		if (!outputLink && typeof(data) === 'string') {
			appConfig.notifyError(data);
		}

		if (__DEBUG__)
			console.debug('Denoise azure result', { data, azureUrl, outputLink });

		return audio;
	} finally {
		if (azureBlob) {
			deleteBlob(azureBlob?.FileName, azureBlob?.AzureContainer);
		}
	}
}

async function speechProcessAudio(opts, base64) {
	if (__DEBUG__) {
		console.debug('processInsertionTranslate speechProcessAudio', { opts, base64 });
	}

	const base64Info = base64 && splitDataUri(base64);
	const ext = getBase64AudioExtension(base64 || '');

	return await postExternalApi('SpeechProcessingProfile/ProcessAudio', {
		ProfileCode: opts?.ProfileCode || 'SsmlTranslator',
		Language: opts?.Language,
		TranslateTo: opts?.TranslateTo,
		AzureFile: opts?.AzureFile,
		FileExtension: ext,
		NeedValidateAudio: opts?.NeedValidateAudio !== void 0 ? opts?.NeedValidateAudio : true,
		Base64StringData: base64Info?.base64
	}).then(r => opts?.AllSteps ? r : r?.FinalText);
}

function log(msg, opts) {
	const style = `background: ${opts?.bg || 'none'}; color: ${opts?.color || '#333'}; padding: 1px 4px; border-radius: 4px;`;
	const log = [`%c${msg}`, style];
	if (arguments?.length > 2) {
		log.push(...[...arguments].slice(2, 999).filter(f => f));
	}

	if (__DEBUG__ || opts?.always)
		console.debug(...log);
}

function timerPromise(func, opts) {
	let res = null;
	let rej = null;
	let lastPromiseActivityTime = Date.now();
	let update = () => {
		lastPromiseActivityTime = Date.now();
	}
	let promise = new Promise((resolve, reject) => {
		res = resolve;
		rej = reject; 
		
		return func(resolve, reject, update);
	});

	let idle = opts?.idle || 60 * 2 * 1000;
	
	let interval = setInterval(() =>{
		if ((Date.now() - lastPromiseActivityTime) >= idle) {
			clearInterval(interval);
			if (opts?.onTimeout) {
				opts.onTimeout();
			}
			if (opts?.resolveOnTimeout) {
				res(opts?.resolveOnTimeout);
			} else {
				rej('timeout');
			}
		}
	}, opts?.checkInterval || 1000)

	return promise;
}

function limitText(text, length) {
	text = text || '';
	if (text.length > length) {
		return text.substr(0, length) + '..';
	}
	return text;
}

async function proxyDownload(url, opts) {
	if (__DEBUG__)
		console.debug('Proxy download', { url, opts });

	var result = (await axios.post('https://httprestream.azurewebsites.net/api/ReceiveStream', {
		url
	}, {
		headers: {
			'Content-Type': 'multipart/form-data',
			authToken: Auth.accessToken(),
			'x-functions-key': '3dO2F9-gh79Z2tQJjvJDlkdKTg8IyU7_2aczm-7lc_nNAzFupJO_Ig==',
			...__HEADERS__
		},
		onDownloadProgress: (progressEvent) => {
			var percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
			if (opts?.onProgress) {
				opts.onProgress(percentCompleted, progressEvent);
			}
		},
		...(opts || {})
	}).then(r => {
		if (__DEBUG__)
			console.debug('Download data response', { r });
		return r;
	}))?.data;

	return result;
}

function processPersistentScheduleCase(schedule, toLower) {
	if (toLower) {
		if (schedule.JsonConfig && typeof(schedule.JsonConfig) === 'string') {
			let scheduleJSON = JSON.parse(schedule.JsonConfig);
			schedule.JsonConfig = {
				...formatObjectToLowerCase(scheduleJSON),
				newsML: {
					...formatObjectToLowerCase(scheduleJSON.NewsML),
					ftpImportOptions: formatObjectToLowerCase(scheduleJSON?.NewsML?.FtpImportOptions || {}),
				},
				templateFields: (scheduleJSON.TemplateFields || []).map(f => formatObjectToLowerCase(f)),
			}
		}
	} else {
		let scheduleJSON = schedule.JsonConfig || {};
		scheduleJSON = {
			...formatObjectToUpperCase(scheduleJSON),
			NewsML: {
				...formatObjectToUpperCase(scheduleJSON.newsML),
				FtpImportOptions: formatObjectToUpperCase(scheduleJSON.newsML.ftpImportOptions),
			},
			TemplateFields: (scheduleJSON.templateFields || []).map(f => formatObjectToUpperCase(f)),
		}
		schedule.JsonConfig = scheduleJSON;
	}
}

function scrapWebsite(url, returnJson) {
	if (!url.startsWith('http')) {
		url = `http://${url}`;
	}

	return axios.get(`${Auth.userInfo.user.WebScraperFunctionUrl}&url=${encodeURIComponent(url)}${returnJson ? '&json=1' : ''}`).then(r => r.data);
}

export {
	getExternalApi,
	postExternalApi,
	getFilterCriteriaSortingParams,
	linkUnlink,
	appConfig,
	dateToString,
	getArrayBufferFromBlob,
	uuid,
	getPublicPath,
	blob2Base64,
	base64toBlob,
	dataURItoBlob,
	getMethodUrl,
	splitDataUri,
	grabMedia,
	denoiseMedia,
	speechProcessAudio,
	capitalize,
	idToReadable,
	log,
	getUtcDate,
	timerPromise,
	limitText,
	proxyDownload,
	encodeHTML,
	getHash,
	scrapWebsite,
	formatNumber
}

export default {
	callMethod,
	loadCategories,
	loadProducts,
	formatNumber,
	getCampaigns,
	getDeliveredCampaignData,
	getCampaign,
	getCartoSql,
	getRadioData,
	getExternalCampaignCreatives,
	formatLocaleCurrency,
	currencySign,
	getPodcasts,
	getEducationLevels,
	getStreamings,
	getNetworks,
	getAdPlacements,
	formatDate,
	getBlob,
	postMethod,
	getMethodUrl,
	getInsertionsByCreative,
	getSsmlInsertionsByEmail,
	createUpdateInsertion,
	createUpdatePureInsertion,
	createUpdateSsmlInsertion,
	createUpdateAudioInsertion,
	loadEmployees,
	loadCompanies,
	formatObjectToLowerCase,
	formatObjectToUpperCase,
	getBase64AudioExtension,
	createUpdateCreative,
	getCountries,
	getStates,
	getCities,
	getLocationGeoJson,
	toggleSelectedIdInArray,
	getPackages,
	createProposal,
	getProposals,
	getRatingMethods,
	spaceByUpperCase,
	getCompany,
	xmlToJson,
	uuid,
	postQuoteApi,
	getQuoteApi,
	postExternalApi,
	getExternalApi,
	getInstructionsByCampaign,
	getMarkets,
	getFormats,
	getSpeechVoices,
	getVoiceDetails,
	getRules,
	getExternalService,
	getExternalServices,
	getOwnExternalServices,
	getWebServiceCategories,
	externalServiceGetParamValue,
	getExternalCampaigns,
	getExternalCampaign,
	getExternalCompanies,
	getAllCreatives,
	getCreativeTemplates,
	getCurrencies,
	getLanguages,
	getMaterialLinks,
	getDynamicDatasets,
	getExternalStations,
	getExternalCompanyStations,
	getExternalNetworks,
	getExternalCountries,
	getExternalCountryRegions,
	getExternalZones,
	getExternalStationFormats,
	getCurrentUserProfile,
	getCurrentUserPhoto,
	getCompanyContacts,
	getLicenseRequests,
	getBrowserLocationByIp,
	getStationStreaminEncoders,
	getInvoices,
	getInvoiceUploadings,
	getInvoicesLog,
	getCustomerInvoices,
	downloadCustomerInvoice,
	getHostAddress,
	getAzureStationLog,
	getAzureStationLogConfig,
	getAzureSettings,
	getReconciliationCampaigns,
	getMySubscriptions,
	getSubscriptionPlans,
	getStripeKey,
	clearCache,
	getAllLocales,
	getTranslations,
	updateTranslations,
	deleteTranslations,
	addLocale,
	validateEmail,
	getUtcDate,
	getCreativePublishHistory,
	getGeoTargets,
	postAudioApi,
	getAudioApi,
	getBase64ContentType,
	getAudioApiUrl,
	readBinaryJSONResponse,
	cloneCreative,
	getTargetProjects,
	getTargetProjectLinkedGeoTargets,
	getActiveCompanyInvites,
	getRevisionsFromString,
	getTimeZones,
	getTimeZonesTimes,
	loadLexicons,
	loadCustomTNs,
	loadStylizers,
	getTemplateCategories,
	langmapItems,
	getHash,
	safe,
	blob2Base64,
	base64toBlob,
	dataURItoBlob,
	splitDataUri,
	downloadURI,
	safeStringify,
	langmap,
	loadIabCategories,
	getUsedLanguages,
	getLanguageNameByCode,
	...campaignData,
	...networkShowData,
	generateMethodCaller,
	objectTypes,
	dateToString,
	capitalize,
	idToReadable,
	formatStringAsKey,
	complexExternalServicesTypes,
	getNextNaming,
	externalServicesParametersDataTypes,
	externalServicesOutputsDataTypes,
	getFilterCriteriaSortingParams,
	linkUnlink,
	secondsToDisplayValue,
	getArrayBufferFromBlob,
	getAudioBufferFromBlob,
	encodeHTML,
	decodeHTML,
	htmlToText,
	getBrowserInfo,
	getPublicPath,
	sleep,
	grabMedia,
	denoiseMedia,
	speechProcessAudio,
	log,
	timerPromise,
	limitText,
	proxyDownload,
	processPersistentScheduleCase,
	scrapWebsite,
	mixin: {
		methods: {
			formatNumber,
			formatDate,
			formatLocaleCurrency,
			currencySign,
			toggleSelectedIdInArray
		}
	}
};
